interface UISelectionEntry {
    available:number[];
    setted: {
        id: number;
        selected?: boolean;
    }[];
}

interface IUISelection {
    purposes: UISelectionEntry;
    specialFeatures: UISelectionEntry;
    purposesLI: UISelectionEntry;
    vendors: UISelectionEntry;
    vendorsLI: UISelectionEntry;
    additionalVendors: UISelectionEntry;
    customVendors: UISelectionEntry;
}

interface IUISettings {
    purposes: NodeJS.Dict<boolean|undefined>;
    specialFeatures: NodeJS.Dict<boolean|undefined>;
    purposesLI: NodeJS.Dict<boolean|undefined>;
    vendors: NodeJS.Dict<boolean|undefined>;
    vendorsLI: NodeJS.Dict<boolean|undefined>;
    additionalVendors: NodeJS.Dict<boolean|undefined>;
    customVendors: NodeJS.Dict<boolean|undefined>;
}

/**
 * Refleja la selección del usuario en la interfaz.
 * No tiene implicaciones de negocio.
 * @class UISelection
 * @implements IUISelection
 */
class UISelection implements IUISelection {

    /**
     * Información de propósitos establecidos por el usuario.
     * Aceptados y Rechazados (pero no los indefinidos).
     * @private
     */
    private _settings: IUISettings = {
        purposes: {},
        specialFeatures: {},
        purposesLI: {},
        vendors: {},
        vendorsLI: {},
        additionalVendors: {},
        customVendors: {}
    };
    public constructor(private readonly _data: IUISelection) {
        this._data.purposes.available.forEach(p => {
            this._settings.purposes[p] = this._data.purposes.setted.find(p2 => p2.id == p)?.selected
        });
        this._data.specialFeatures.available.forEach(p => {
            this._settings.specialFeatures[p] = this._data.specialFeatures.setted.find(p2 => p2.id == p)?.selected
        });
        this._data.purposesLI.available.forEach(p => {
            this._settings.purposesLI[p] = this._data.purposesLI.setted.find(p2 => p2.id == p)?.selected
        });
        this._data.vendors.available.forEach(p => {
            this._settings.vendors[p] = this._data.vendors.setted.find(p2 => p2.id == p)?.selected
        });
        this._data.vendorsLI.available.forEach(p => {
            this._settings.vendorsLI[p] = this._data.vendorsLI.setted.find(p2 => p2.id == p)?.selected
        });
        this._data.additionalVendors.available.forEach(p => {
            this._settings.additionalVendors[p] = this._data.additionalVendors.setted.find(p2 => p2.id == p)?.selected
        });
        this._data.customVendors.available.forEach(p => {
            this._settings.customVendors[p] = this._data.customVendors.setted.find(p2 => p2.id == p)?.selected
        });
    }

    public get purposes() {
        return this._data.purposes;
    }

    public get specialFeatures() {
        return this._data.specialFeatures;
    }

    public get purposesLI() {
        return this._data.purposesLI;
    }

    public get vendors() {
        return this._data.vendors;
    }

    public get vendorsLI() {
        return this._data.vendorsLI;
    }

    public get additionalVendors() {
        return this._data.additionalVendors;
    }

    public get customVendors() {
        return this._data.customVendors;
    }

    public allSetted() : boolean {
        return this.purposes.available.length == this.purposesSetted().length
            && this.specialFeatures.available.length == this.specialFeaturesSetted().length
            && this.purposesLI.available.length == this.purposesLISetted().length
            && this.vendors.available.length == this.vendorsSetted().length
            && this.vendorsLI.available.length == this.vendorsLISetted().length
            && this.additionalVendors.available.length == this.additionalVendorsSetted().length
            && this.customVendors.available.length == this.customVendorsSetted().length
        ;
    }

    private purposesSetted() : number[] {
        const setted: number[] = [];
        Object.keys(this._settings.purposes).forEach(k => {
            if (this._settings.purposes[k] !== undefined) setted.push(parseInt(k))
        });
        return setted;
    }

    private specialFeaturesSetted() : number[] {
        const setted: number[] = [];
        Object.keys(this._settings.specialFeatures).forEach(k => {
            if (this._settings.specialFeatures[k] !== undefined) setted.push(parseInt(k))
        });
        return setted;
    }

    private purposesLISetted() : number[] {
        const setted: number[] = [];
        Object.keys(this._settings.purposesLI).forEach(k => {
            if (this._settings.purposesLI[k] !== undefined) setted.push(parseInt(k))
        });
        return setted;
    }

    private vendorsSetted() : number[] {
        const setted: number[] = [];
        Object.keys(this._settings.vendors).forEach(k => {
            if (this._settings.vendors[k] !== undefined) setted.push(parseInt(k))
        });
        return setted;
    }

    private vendorsLISetted() : number[] {
        const setted: number[] = [];
        Object.keys(this._settings.vendorsLI).forEach(k => {
            if (this._settings.vendorsLI[k] !== undefined) setted.push(parseInt(k))
        });
        return setted;
    }

    private additionalVendorsSetted() : number[] {
        const setted: number[] = [];
        Object.keys(this._settings.additionalVendors).forEach(k => {
            if (this._settings.additionalVendors[k] !== undefined) setted.push(parseInt(k))
        });
        return setted;
    }

    private customVendorsSetted() : number[] {
        const setted: number[] = [];
        Object.keys(this._settings.customVendors).forEach(k => {
            if (this._settings.customVendors[k] !== undefined) setted.push(parseInt(k))
        });
        return setted;
    }

    public setPurpose(purposeId: number, value: boolean): void {
        this._settings.purposes[purposeId] = value;
    }

    public setPurposeLI(purposeId: number, value: boolean): void {
        this._settings.purposesLI[purposeId] = value;
    }

    public setSpecialFeature(featureId: number, value: boolean): void {
        this._settings.specialFeatures[featureId] = value;
    }

    public setVendor(vendorId: number, value: boolean): void {
        this._settings.vendors[vendorId] = value;
    }

    public setVendorLI(vendorId: number, value: boolean): void {
        this._settings.vendorsLI[vendorId] = value;
    }

    public setAdditionalVendor(vendorId: number, value: boolean): void {
        this._settings.additionalVendors[vendorId] = value;
    }

    public setCustomVendor(vendorId: number, value: boolean): void {
        this._settings.customVendors[vendorId] = value;
    }

    public setAllPurposes(value: boolean): void {
        this._data.purposes.available.forEach(p => this._settings.purposes[p] = value);
        this._data.specialFeatures.available.forEach(p => this._settings.specialFeatures[p] = value);
    }

    public setAllPurposesLI(value: boolean): void {
        this._data.purposesLI.available.forEach(p => this._settings.purposesLI[p] = value);
    }

    public setAllVendors(value: boolean): void {
        this._data.vendors.available.forEach(p => this._settings.vendors[p] = value);
        this._data.vendorsLI.available.forEach(p => this._settings.vendorsLI[p] = value);
        this._data.additionalVendors.available.forEach(p => this._settings.additionalVendors[p] = value);
        this._data.customVendors.available.forEach(p => this._settings.customVendors[p] = value);
    }

    public areAllSelected() : boolean {
        return this._data.purposes.available.every(p => this._settings.purposes[p] == true)
            && this._data.specialFeatures.available.every(p => this._settings.specialFeatures[p] == true)
            && this._data.purposesLI.available.every(p => this._settings.purposesLI[p] == true)
            && this._data.vendors.available.every(p => this._settings.vendors[p] == true)
            && this._data.vendorsLI.available.every(p => this._settings.vendorsLI[p] == true)
            && this._data.additionalVendors.available.every(p => this._settings.additionalVendors[p] == true)
            && this._data.customVendors.available.every(p => this._settings.customVendors[p] == true)
        ;
    }

    public areAllRejected(): boolean {
        return this._data.purposes.available.every(p => this._settings.purposes[p] == false)
            && this._data.specialFeatures.available.every(p => this._settings.specialFeatures[p] == false)
            && this._data.purposesLI.available.every(p => this._settings.purposesLI[p] == false)
            && this._data.vendors.available.every(p => this._settings.vendors[p] == false)
            && this._data.vendorsLI.available.every(p => this._settings.vendorsLI[p] == false)
            && this._data.additionalVendors.available.every(p => this._settings.additionalVendors[p] == false)
            && this._data.customVendors.available.every(p => this._settings.customVendors[p] == false)
        ;
    }

    public isVendorSelected(vendorId: number): boolean|undefined {
        return this._settings.vendors[vendorId];
    }

    public isVendorLISelected(vendorId: number): boolean|undefined {
        return this._settings.vendorsLI[vendorId];
    }

    public isAdditionalVendorSelected(vendorId: number): boolean|undefined {
        return this._settings.additionalVendors[vendorId];
    }

    public isCustomVendorSelected(vendorId: number): boolean|undefined {
        return this._settings.customVendors[vendorId];
    }
}

export {UISelection};
